import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

async function geocodeAddress({ street, city, state, postalCode, country }) {
    const parts = [street, city, state, postalCode, country].filter(Boolean);
    const query = encodeURIComponent(parts.join(', '));
    try {
        const res = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`,
            { headers: { 'Accept-Language': 'en', 'User-Agent': 'sharc.fm venue geocoder' } }
        );
        const data = await res.json();
        if (data && data.length > 0) {
            return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        }
    } catch (e) {
        console.warn('Geocoding failed:', e);
    }
    return { lat: null, lng: null };
}

function EditVenue() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [picklists, setPicklists] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dupWarning, setDupWarning] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [venueId, setVenueId] = useState(null);
    const [venueSlug, setVenueSlug] = useState(slug);
    const [sharcId, setSharcId] = useState('');
    const [sharcIdStatus, setSharcIdStatus] = useState(null);
    const [links, setLinks] = useState([]);

    const [form, setForm] = useState({
        name: '',
        street: '',
        city: '',
        country_id: '',
        state_id: '',
        postal_code: '',
        status_id: '',
        venue_type_id: '',
        capacity: '',
        age_limit_id: '',
        description: '',
        image_url: '',
        lat: '',
        lng: '',
        plus_code: ''
    });

    useEffect(() => {
        Promise.all([
            axios.get(`${config.API_URL}/venues/${slug}`),
            axios.get(`${config.API_URL}/picklists`)
        ]).then(([venueRes, picklistsRes]) => {
            const venue = venueRes.data.venue;
            setVenueId(venue.id);
            setVenueSlug(venue.slug || slug);
            setSharcId(venue.sharc_id || '');
            setLinks(venueRes.data.links || []);
            setForm({
                name: venue.name || '',
                street: venue.street || '',
                city: venue.city || '',
                country_id: venue.country_id || '',
                state_id: venue.state_id || '',
                postal_code: venue.postal_code || '',
                status_id: venue.status_id || '',
                venue_type_id: venue.venue_type_id || '',
                capacity: venue.capacity || '',
                age_limit_id: venue.age_limit_id || '',
                description: venue.description || '',
                image_url: venue.image_url || '',
                lat: venue.lat != null ? String(venue.lat) : '',
                lng: venue.lng != null ? String(venue.lng) : '',
                plus_code: venue.plus_code || ''
            });
            setPicklists(picklistsRes.data);
            setLoading(false);
        }).catch(() => {
            setError('Failed to load venue data');
            setLoading(false);
        });
    }, [slug]);

    // SHARC ID availability check (debounced)
    useEffect(() => {
        if (!sharcId) { setSharcIdStatus(null); return; }
        const valid = /^[a-z0-9-]+$/.test(sharcId);
        if (!valid) { setSharcIdStatus('invalid'); return; }
        setSharcIdStatus('checking');
        const timer = setTimeout(() => {
            axios.get(`${config.API_URL}/check-sharc-id`, {
                params: { sharc_id: sharcId, exclude_id: venueId, exclude_type: 'venue' }
            }).then(res => {
                setSharcIdStatus(res.data.available ? 'available' : 'taken');
            }).catch(() => setSharcIdStatus(null));
        }, 500);
        return () => clearTimeout(timer);
    }, [sharcId, venueId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'country_id' ? { state_id: '' } : {})
        }));
    };

    // Links handlers
    const addLink = () => setLinks(prev => [...prev, { description: '', url: '' }]);
    const updateLink = (index, field, value) => {
        setLinks(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
    };
    const removeLink = (index) => setLinks(prev => prev.filter((_, i) => i !== index));

    const filteredStates = picklists
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    const handleSubmit = () => {
        const required = ['name', 'city', 'country_id', 'state_id', 'status_id', 'venue_type_id', 'capacity'];
        const missing = required.filter(f => !form[f]);
        if (missing.length > 0) {
            setError('Please fill in all required fields');
            return;
        }
        setError(null);
        setShowConfirm(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setDupWarning(null);
        setShowConfirm(false);

        // If lat/lng are blank, attempt to geocode from address
        let lat = form.lat !== '' ? form.lat : null;
        let lng = form.lng !== '' ? form.lng : null;
        if (lat === null || lng === null) {
            const stateObj = picklists.states_regions.find(s => s.id === parseInt(form.state_id));
            const countryObj = picklists.countries.find(c => c.id === parseInt(form.country_id));
            const geocoded = await geocodeAddress({
                street: form.street,
                city: form.city,
                state: stateObj?.name,
                postalCode: form.postal_code,
                country: countryObj?.name,
            });
            lat = geocoded.lat;
            lng = geocoded.lng;
        }

        try {
            const { tokens } = await fetchAuthSession();
            const idToken = tokens.idToken.toString();
            const payload = {
                name: form.name,
                street: form.street || null,
                city: form.city,
                country_id: parseInt(form.country_id),
                state_id: parseInt(form.state_id),
                postal_code: form.postal_code || null,
                status_id: parseInt(form.status_id),
                venue_type_id: parseInt(form.venue_type_id),
                capacity: parseInt(form.capacity),
                age_limit_id: parseInt(form.age_limit_id),
                sharc_id: sharcId.trim() || null,
                description: form.description || null,
                image_url: form.image_url || null,
                lat,
                lng,
                plus_code: form.plus_code || null,
                links: links.filter(l => l.description.trim() && l.url.trim()),
            };
            const res = await axios.put(`${config.API_URL}/venues/${venueId}`, payload, { headers: { Authorization: idToken } });
            const newSlug = res.data.slug || venueSlug;
            navigate(`/venues/${newSlug}`);
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'duplicate') {
                setDupWarning(err.response.data.existing);
            } else {
                setError('Failed to save changes');
            }
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <div style={styles.container}>
            <Link to={`/venues/${venueSlug}`} style={styles.backLink}>← Back to Venue</Link>
            <h1 style={styles.title}>Edit Venue</h1>

            {error && <div style={styles.error}>{error}</div>}
            {dupWarning && (
                <div style={styles.dupBox}>
                    <strong>⚠ Duplicate venue detected.</strong> Another venue with this name, city, and location already exists:
                    <div style={{ marginTop: '8px' }}>
                        <strong>{dupWarning.name}</strong>
                        {(dupWarning.city || dupWarning.state_name || dupWarning.country_name) && (
                            <span> — {[dupWarning.city, dupWarning.state_name, dupWarning.country_name].filter(Boolean).join(', ')}</span>
                        )}
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <a href={`/venues/${dupWarning.id}`} style={styles.dupLink}>→ View existing venue</a>
                    </div>
                </div>
            )}

            <div style={styles.form}>

                {/* Core Info */}
                <div style={styles.field}>
                    <label style={styles.label}>Venue Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Street Address</label>
                    <input name="street" value={form.street} onChange={handleChange} style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>City *</label>
                    <input name="city" value={form.city} onChange={handleChange} style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Country *</label>
                    <select name="country_id" value={form.country_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select country...</option>
                        {picklists.countries.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {form.country_id && filteredStates.length > 0 && (
                    <div style={styles.field}>
                        <label style={styles.label}>State / Region *</label>
                        <select name="state_id" value={form.state_id} onChange={handleChange} style={styles.input}>
                            <option value="">Select state/region...</option>
                            {filteredStates.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={styles.field}>
                    <label style={styles.label}>Postal Code</label>
                    <input name="postal_code" value={form.postal_code} onChange={handleChange} style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Venue Status *</label>
                    <select name="status_id" value={form.status_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select status...</option>
                        {picklists.venue_statuses.map(s => (
                            <option key={s.id} value={s.id}>{s.value}</option>
                        ))}
                    </select>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Venue Type *</label>
                    <select name="venue_type_id" value={form.venue_type_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select type...</option>
                        {picklists.venue_types.map(t => (
                            <option key={t.id} value={t.id}>{t.value}</option>
                        ))}
                    </select>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Capacity *</label>
                    <input name="capacity" type="number" value={form.capacity} onChange={handleChange} style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Age Restriction (Default)</label>
                    <select name="age_limit_id" value={form.age_limit_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select age restriction...</option>
                        {picklists.venue_age_limits.map(a => (
                            <option key={a.id} value={a.id}>{a.value}</option>
                        ))}
                    </select>
                </div>

                {/* Bio & Image */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Bio & Image</div>

                <div style={styles.field}>
                    <label style={styles.label}>Description / Bio</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={5}
                        placeholder="A brief description of the venue..."
                        style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.6' }}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Image URL</label>
                    <input
                        name="image_url"
                        value={form.image_url}
                        onChange={handleChange}
                        placeholder="https://..."
                        style={styles.input}
                    />
                    {form.image_url && (
                        <div style={{ marginTop: '12px' }}>
                            <img
                                src={form.image_url}
                                alt="Preview"
                                style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #444' }}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                </div>

                {/* Geolocation */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Geolocation</div>
                <div style={styles.sectionHint}>
                    Coordinates are set automatically from the address. To override, enter values manually below — your values will be used as-is. Clear both fields to re-geocode from the address on next save. Plus Code example: <span style={{ color: '#bb86fc' }}>87G8+QP</span>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Google Maps Plus Code</label>
                    <input
                        name="plus_code"
                        value={form.plus_code}
                        onChange={handleChange}
                        placeholder="e.g. 87G8+QP"
                        style={styles.input}
                    />
                </div>

                <div style={styles.twoCol}>
                    <div style={styles.field}>
                        <label style={styles.label}>Latitude</label>
                        <input
                            name="lat"
                            type="number"
                            step="any"
                            value={form.lat}
                            onChange={handleChange}
                            placeholder="e.g. 27.9506"
                            style={styles.input}
                        />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Longitude</label>
                        <input
                            name="lng"
                            type="number"
                            step="any"
                            value={form.lng}
                            onChange={handleChange}
                            placeholder="e.g. -82.4572"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* Links */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Links</div>
                <div style={styles.sectionHint}>Website, social media, ticketing, etc.</div>

                {links.map((link, index) => (
                    <div key={index} style={styles.repeatCard}>
                        <div style={styles.repeatCardRow}>
                            <div style={{ flex: '0 0 160px' }}>
                                <label style={styles.miniLabel}>Label</label>
                                <input
                                    value={link.description}
                                    onChange={e => updateLink(index, 'description', e.target.value)}
                                    placeholder="e.g. Website"
                                    style={styles.input}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={styles.miniLabel}>URL</label>
                                <input
                                    value={link.url}
                                    onChange={e => updateLink(index, 'url', e.target.value)}
                                    placeholder="https://..."
                                    style={styles.input}
                                />
                            </div>
                            <button onClick={() => removeLink(index)} style={{ ...styles.removeButton, alignSelf: 'flex-end', marginBottom: '1px' }}>✕</button>
                        </div>
                    </div>
                ))}
                <button onClick={addLink} style={styles.addRowButton}>+ Add Link</button>

                {/* SHARC ID */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>SHARC ID</div>
                <div style={styles.sectionHint}>
                    Give this venue a vanity URL — e.g. "geeks" means fans can visit <span style={{ color: '#bb86fc' }}>sharc-events.com/geeks</span> directly. Must be lowercase, no spaces (dashes OK). Leave blank to use the default URL.
                </div>
                <div style={styles.field}>
                    <label style={styles.label}>SHARC ID</label>
                    <input
                        value={sharcId}
                        onChange={e => setSharcId(e.target.value.toLowerCase())}
                        placeholder="e.g. geeks"
                        style={{
                            ...styles.input,
                            borderColor: sharcIdStatus === 'taken' || sharcIdStatus === 'invalid' ? '#cf6679'
                                : sharcIdStatus === 'available' ? '#4caf7d' : '#444'
                        }}
                    />
                    {sharcIdStatus === 'checking' && <div style={styles.sharcChecking}>Checking availability...</div>}
                    {sharcIdStatus === 'available' && <div style={styles.sharcAvailable}>✓ Available — sharc-events.com/{sharcId}</div>}
                    {sharcIdStatus === 'taken' && <div style={styles.sharcTaken}>✕ Already taken</div>}
                    {sharcIdStatus === 'invalid' && <div style={styles.sharcTaken}>✕ Only lowercase letters, numbers, and dashes allowed</div>}
                </div>

                <div style={styles.buttonRow}>
                    <button onClick={() => navigate(`/venues/${venueSlug}`)} style={styles.cancelButton}>Cancel</button>
                    <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {showConfirm && (
                <div style={styles.overlay}>
                    <div style={styles.modal}>
                        <h2 style={styles.modalTitle}>Confirm Changes</h2>
                        <p style={styles.modalText}>
                            Are you sure you want to save changes to <strong style={{color: '#bb86fc'}}>{form.name}</strong>?
                        </p>
                        <div style={styles.modalButtons}>
                            <button onClick={() => setShowConfirm(false)} style={styles.cancelButton}>Go Back</button>
                            <button onClick={handleSave} style={styles.submitButton}>Yes, Save Changes</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: { maxWidth: '600px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '32px' },
    field: { marginBottom: '24px' },
    label: { display: 'block', fontSize: '14px', color: '#aaa', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    miniLabel: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    sectionDivider: { borderTop: '1px solid #333', margin: '8px 0 24px' },
    sectionHeader: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '8px' },
    sectionHint: { fontSize: '13px', color: '#888', marginBottom: '20px', lineHeight: '1.5' },
    repeatCard: { backgroundColor: '#242424', border: '1px solid #383838', borderRadius: '6px', padding: '16px', marginBottom: '12px' },
    repeatCardRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
    removeButton: { backgroundColor: 'transparent', border: '1px solid #555', borderRadius: '4px', color: '#888', fontSize: '14px', padding: '8px 10px', cursor: 'pointer', flexShrink: 0 },
    addRowButton: { backgroundColor: 'transparent', border: '1px dashed #555', borderRadius: '4px', color: '#bb86fc', fontSize: '14px', padding: '8px 16px', cursor: 'pointer', width: '100%', marginBottom: '8px' },
    buttonRow: { display: 'flex', gap: '12px', marginTop: '8px' },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', flex: 1 },
    cancelButton: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', cursor: 'pointer', flex: 1 },
    error: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '12px 16px', color: '#cf6679', marginBottom: '24px', fontSize: '14px' },
    dupBox: { backgroundColor: '#1e1a0e', border: '1px solid #f0a500', borderRadius: '4px', padding: '14px 16px', color: '#f0c060', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' },
    dupLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    sharcChecking: { fontSize: '13px', color: '#888', marginTop: '6px' },
    sharcAvailable: { fontSize: '13px', color: '#4caf7d', marginTop: '6px' },
    sharcTaken: { fontSize: '13px', color: '#cf6679', marginTop: '6px' },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '12px', padding: '32px', maxWidth: '440px', width: '90%' },
    modalTitle: { fontSize: '22px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '16px' },
    modalText: { fontSize: '16px', color: '#aaa', marginBottom: '28px', lineHeight: '1.5' },
    modalButtons: { display: 'flex', gap: '12px' },
};

export default EditVenue;